import { BookOpen, Clock, Flame, SlidersHorizontal, Star } from 'lucide-react';
import type { PracticeScope } from '@/lib/practice/session-types';
import type { SessionRecord } from '@/hooks/mindspace/usePracticeHistory';

// Both switches are exhaustive on purpose — no `default`. A `default` let a
// new scope (`starred`) fall through to the custom label silently; without
// one, TypeScript flags any scope added to `PracticeScope` and left out here.
export function scopeIcon(scope: PracticeScope) {
  switch (scope) {
    case 'due':
      return Clock;
    case 'weak':
      return Flame;
    case 'recent':
      return BookOpen;
    case 'starred':
      return Star;
    case 'custom':
      return SlidersHorizontal;
  }
}

export function scopeLabel(scope: PracticeScope): string {
  switch (scope) {
    case 'due':
      return 'Due today';
    case 'weak':
      return 'Weak concepts';
    case 'recent':
      return 'Recent lesson';
    case 'starred':
      return 'Starred';
    case 'custom':
      return 'Custom session';
  }
}

export function formatRelativeTime(endedAt: number): string {
  const diffMs = Date.now() - endedAt;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  return new Date(endedAt).toLocaleDateString();
}

export function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

export function gradeLabel(grade: 1 | 2 | 3 | 4): string {
  switch (grade) {
    case 1:
      return 'Again';
    case 2:
      return 'Hard';
    case 3:
      return 'Good';
    case 4:
      return 'Easy';
  }
}

export function gradeTone(
  grade: 1 | 2 | 3 | 4
): 'emerald' | 'indigo' | 'amber' | 'rose' {
  switch (grade) {
    case 4:
      return 'emerald';
    case 3:
      return 'indigo';
    case 2:
      return 'amber';
    case 1:
      return 'rose';
  }
}

/**
 * Per-row timestamp formatter for the history sidebar.
 *
 *   - same day as `now`        → `2:34 PM`
 *   - same year as `now`       → `Nov 28`
 *   - different year           → `Nov 28, 2024`
 *
 * `now` is a parameter so the function stays pure + testable.
 */
export function formatRowTime(
  endedAt: number,
  now: number = Date.now()
): string {
  const then = new Date(endedAt);
  const today = new Date(now);
  const sameDay =
    then.getFullYear() === today.getFullYear() &&
    then.getMonth() === today.getMonth() &&
    then.getDate() === today.getDate();

  if (sameDay) {
    return then.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (then.getFullYear() === today.getFullYear()) {
    return then.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
  return then.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Bucket a chronologically-sorted (newest-first) list of session
 * records into ChatGPT-style date groups. Returns ONLY non-empty
 * groups, in display order. Older items roll up into per-month buckets
 * (`November 2025`, `October 2025`, …).
 */
export function groupHistoryByDate(
  records: SessionRecord[],
  now: number = Date.now()
): Array<{ label: string; items: SessionRecord[] }> {
  if (records.length === 0) return [];

  const today = new Date(now);
  const startOfToday = new Date(today);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTodayMs = startOfToday.getTime();
  const startOfYesterdayMs = startOfTodayMs - 24 * 60 * 60 * 1000;
  const startOfPrev7Ms = startOfTodayMs - 7 * 24 * 60 * 60 * 1000;
  const startOfPrev30Ms = startOfTodayMs - 30 * 24 * 60 * 60 * 1000;

  const todayItems: SessionRecord[] = [];
  const yesterdayItems: SessionRecord[] = [];
  const prev7Items: SessionRecord[] = [];
  const prev30Items: SessionRecord[] = [];
  const monthBuckets = new Map<string, SessionRecord[]>();

  for (const r of records) {
    if (r.endedAt >= startOfTodayMs) {
      todayItems.push(r);
    } else if (r.endedAt >= startOfYesterdayMs) {
      yesterdayItems.push(r);
    } else if (r.endedAt >= startOfPrev7Ms) {
      prev7Items.push(r);
    } else if (r.endedAt >= startOfPrev30Ms) {
      prev30Items.push(r);
    } else {
      const d = new Date(r.endedAt);
      // Use UTC-stable key so two records in the same calendar month
      // share a bucket regardless of TZ wobble.
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
      const list = monthBuckets.get(key) ?? [];
      list.push(r);
      monthBuckets.set(key, list);
    }
  }

  const groups: Array<{ label: string; items: SessionRecord[] }> = [];
  if (todayItems.length) groups.push({ label: 'Today', items: todayItems });
  if (yesterdayItems.length)
    groups.push({ label: 'Yesterday', items: yesterdayItems });
  if (prev7Items.length)
    groups.push({ label: 'Previous 7 days', items: prev7Items });
  if (prev30Items.length)
    groups.push({ label: 'Previous 30 days', items: prev30Items });

  // Month buckets - sort keys newest-first so December 2025 precedes
  // November 2025. Insertion order from `Map` already matches input
  // order (records are newest-first), so we can iterate as-is.
  for (const [, items] of monthBuckets) {
    const d = new Date(items[0].endedAt);
    const label = d.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    });
    groups.push({ label, items });
  }

  return groups;
}
